import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useUserData } from "../../contexts/user-data-context";
import { useAnalytics } from "../../lib/analytics";
import { api } from "../../lib/api";
import { useClientTranslations } from "../../lib/i18n/client";
import { dispatchProfileImageUpdate } from "../../lib/utils/profile-events";
import styles from "./ProfileImageUploader.module.css";

interface ProfileImageUploaderProps {
	initialImageUrl?: string;
	user?: { id: string; displayName?: string | null };
}

export function ProfileImageUploader({
	initialImageUrl = "",
	user,
}: ProfileImageUploaderProps) {
	const inputId = useId();
	const { logEvent } = useAnalytics();

	// Helper function to check if image is valid
	const isValidImage = (
		imageUrl: string | null | undefined,
	): imageUrl is string => {
		if (
			imageUrl == null ||
			imageUrl.trim() === "" ||
			imageUrl === "null" ||
			imageUrl === "undefined"
		) {
			return false;
		}
		// Reject Gravatar URLs since they show initials fallback
		if (
			imageUrl.includes("gravatar.com") ||
			imageUrl.includes("auth0.com/avatars")
		) {
			return false;
		}
		return true;
	};

	const [previewUrl, setPreviewUrl] = useState<string>(initialImageUrl);
	const [isImageUploading, setIsImageUploading] = useState(false);
	const { updateUserData, userData } = useUserData();
	const { t } = useClientTranslations("profile");

	// Update preview URL when initial image URL changes
	useEffect(() => {
		setPreviewUrl(initialImageUrl);
	}, [initialImageUrl]);

	const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			try {
				setIsImageUploading(true);

				const formData = new FormData();
				formData.append("file", file);

				// Use centralized API client for file uploads
				const response = await api.post<{ url: string; success: boolean }>(
					"/api/upload",
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				);

				const data = response.data;

				setPreviewUrl(data.url);

				// Update user image in database using unified API
				if (user?.id || userData?.id) {
					try {
						await api.post("/api/user/update-image", {
							userImage: data.url,
							userId: user?.id || userData?.id,
						});

						// Dispatch event to notify other components about the profile image update
						const userId = user?.id || userData?.id;
						if (userId) {
							dispatchProfileImageUpdate(userId, data.url);
						}
					} catch (error) {
						console.warn("Failed to update user image in database:", error);
					}
				}

				// Also update context for backward compatibility
				updateUserData({ userImage: data.url });

				logEvent("profile_image_uploaded", undefined, {
					success: true,
					file_size: file.size,
					file_type: file.type,
				});

				toast.success(t("messages.profileImageUpdated"));
			} catch (error) {
				console.error("Error uploading image:", error);
				toast.error(t("messages.profileImageFailed"));

				logEvent("profile_image_upload_failed", undefined, {
					error: error instanceof Error ? error.message : "Unknown error",
					file_size: file.size,
					file_type: file.type,
				});
			} finally {
				setIsImageUploading(false);
			}
		}
	};

	const handleRemoveImage = async () => {
		try {
			setIsImageUploading(true);

			// Update user image in database to null
			if (user?.id || userData?.id) {
				try {
					await api.post("/api/user/update-image", {
						userImage: null,
						userId: user?.id || userData?.id,
					});

					// Dispatch event to notify other components about the profile image removal
					const userId = user?.id || userData?.id;
					if (userId) {
						dispatchProfileImageUpdate(userId, "");
					}
				} catch (error) {
					console.warn("Failed to remove user image in database:", error);
				}
			}

			// Clear preview URL and context
			setPreviewUrl("");
			updateUserData({ userImage: null });

			logEvent("profile_image_removed", undefined, {
				success: true,
			});

			toast.success(t("messages.profileImageRemoved"));
		} catch (error) {
			console.error("Error removing image:", error);
			toast.error(t("messages.profileImageFailed"));

			logEvent("profile_image_remove_failed", undefined, {
				error: error instanceof Error ? error.message : "Unknown error",
			});
		} finally {
			setIsImageUploading(false);
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.imageWrapper}>
				<div className={styles.imageCircle}>
					{isImageUploading ? (
						<div className={styles.loading}>
							<svg
								className={styles.spinner}
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<title>Loading</title>
								<circle
									className={styles.spinnerCircle}
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								/>
								<path
									className={styles.spinnerPath}
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
						</div>
					) : isValidImage(previewUrl) ? (
						<img
							src={previewUrl}
							alt={t("messages.profileImageAlt")}
							className={styles.image}
						/>
					) : (
						<img
							src="/images/era-icon-blue.png"
							alt="Era"
							className={styles.image}
						/>
					)}
				</div>
				<label
					htmlFor={inputId}
					className={`${styles.editButton} ${isImageUploading ? styles.disabled : ""}`}
				>
					<svg
						className={styles.editIcon}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<title>Edit</title>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
						/>
					</svg>
					<input
						id={inputId}
						type="file"
						accept="image/*"
						className={styles.hiddenInput}
						onChange={handleImageChange}
						disabled={isImageUploading}
					/>
				</label>

				{/* Remove button - only show when there's a valid image */}
				{isValidImage(previewUrl) && (
					<button
						type="button"
						onClick={handleRemoveImage}
						disabled={isImageUploading}
						className={`${styles.removeButton} ${isImageUploading ? styles.disabled : ""}`}
						title={t("messages.removeProfilePicture")}
					>
						<svg
							className={styles.removeIcon}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<title>Remove</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
							/>
						</svg>
					</button>
				)}
			</div>
		</div>
	);
}
